import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useToast } from '../../context/ToastContext';
import {
  createAdminCountry,
  createAdminProductGroup,
  createAdminProductName,
  createAdminProductType,
  fetchAdminCountries,
  fetchAdminProductGroups,
  fetchAdminProductNames,
  fetchAdminProductTypes,
  updateAdminCountry,
  updateAdminProductGroup,
  updateAdminProductName,
  updateAdminProductType,
} from '../../services/masterDataApi';

const TABS = [
  { key: 'groups', label: 'Ürün Grupları', icon: 'Layers' },
  { key: 'names', label: 'Ürün Adları', icon: 'Tag' },
  { key: 'types', label: 'Ürün Türleri', icon: 'List' },
  { key: 'countries', label: 'Ülkeler', icon: 'Globe' },
];

const DEFAULT_GROUP_FORM = {
  name: '',
  slug: '',
  icon: '',
  sortOrder: '0',
};

const DEFAULT_NAME_FORM = {
  groupId: '',
  name: '',
  slug: '',
  subCategory: '',
  sortOrder: '0',
};

const DEFAULT_TYPE_FORM = {
  productNameId: '',
  name: '',
  slug: '',
  sortOrder: '0',
};

const DEFAULT_COUNTRY_FORM = {
  name: '',
  isoCode: '',
  sortOrder: '0',
};

const normalizeOptional = (value) => {
  const normalized = `${value || ''}`.trim();
  return normalized.length ? normalized : undefined;
};

const parseSortOrder = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const StatusBadge = ({ isActive }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
      isActive
        ? 'bg-success-100 text-success border border-success'
        : 'bg-secondary-100 text-text-secondary border border-border'
    }`}
  >
    {isActive ? 'Aktif' : 'Pasif'}
  </span>
);

const GeneralManagementSettings = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState(TABS[0].key);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [groups, setGroups] = useState([]);
  const [productNames, setProductNames] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [countries, setCountries] = useState([]);

  const [groupForm, setGroupForm] = useState(DEFAULT_GROUP_FORM);
  const [productNameForm, setProductNameForm] = useState(DEFAULT_NAME_FORM);
  const [productTypeForm, setProductTypeForm] = useState(DEFAULT_TYPE_FORM);
  const [countryForm, setCountryForm] = useState(DEFAULT_COUNTRY_FORM);
  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [groupsData, namesData, typesData, countriesData] = await Promise.all([
        fetchAdminProductGroups({ includeInactive: true }),
        fetchAdminProductNames({ includeInactive: true }),
        fetchAdminProductTypes({ includeInactive: true }),
        fetchAdminCountries({ includeInactive: true }),
      ]);

      setGroups(groupsData || []);
      setProductNames(namesData || []);
      setProductTypes(typesData || []);
      setCountries(countriesData || []);
    } catch (error) {
      toastError(error?.message || 'Yönetim verileri yüklenirken hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const groupOptions = useMemo(
    () =>
      groups.map((group) => ({
        value: group.id,
        label: `${group.name}${group.isActive ? '' : ' (Pasif)'}`,
      })),
    [groups],
  );

  const productNameOptions = useMemo(
    () =>
      productNames.map((item) => ({
        value: item.id,
        label: `${item.group?.name || '-'} / ${item.name}${item.isActive ? '' : ' (Pasif)'}`,
      })),
    [productNames],
  );

  const runMutation = useCallback(
    async (mutation, successMessage) => {
      setIsSaving(true);
      try {
        await mutation();
        toastSuccess(successMessage);
        await loadAll();
        return true;
      } catch (error) {
        toastError(error?.message || 'İşlem sırasında hata oluştu.');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [loadAll, toastError, toastSuccess],
  );

  const handleCreateGroup = async (event) => {
    event.preventDefault();
    if (!groupForm.name.trim()) {
      toastError('Ürün grubu adı zorunludur.');
      return;
    }

    const isSuccess = await runMutation(
      () =>
        createAdminProductGroup({
          name: groupForm.name.trim(),
          slug: normalizeOptional(groupForm.slug),
          icon: normalizeOptional(groupForm.icon),
          sortOrder: parseSortOrder(groupForm.sortOrder),
        }),
      'Ürün grubu eklendi.',
    );

    if (isSuccess) {
      setGroupForm(DEFAULT_GROUP_FORM);
    }
  };

  const handleCreateProductName = async (event) => {
    event.preventDefault();
    if (!productNameForm.groupId) {
      toastError('Önce bir ürün grubu seçin.');
      return;
    }
    if (!productNameForm.name.trim()) {
      toastError('Ürün adı zorunludur.');
      return;
    }

    const isSuccess = await runMutation(
      () =>
        createAdminProductName({
          groupId: productNameForm.groupId,
          name: productNameForm.name.trim(),
          slug: normalizeOptional(productNameForm.slug),
          subCategory: normalizeOptional(productNameForm.subCategory),
          sortOrder: parseSortOrder(productNameForm.sortOrder),
        }),
      'Ürün adı eklendi.',
    );

    if (isSuccess) {
      setProductNameForm((prev) => ({ ...DEFAULT_NAME_FORM, groupId: prev.groupId }));
    }
  };

  const handleCreateProductType = async (event) => {
    event.preventDefault();
    if (!productTypeForm.productNameId) {
      toastError('Önce bir ürün adı seçin.');
      return;
    }
    if (!productTypeForm.name.trim()) {
      toastError('Ürün türü zorunludur.');
      return;
    }

    const isSuccess = await runMutation(
      () =>
        createAdminProductType({
          productNameId: productTypeForm.productNameId,
          name: productTypeForm.name.trim(),
          slug: normalizeOptional(productTypeForm.slug),
          sortOrder: parseSortOrder(productTypeForm.sortOrder),
        }),
      'Ürün türü eklendi.',
    );

    if (isSuccess) {
      setProductTypeForm((prev) => ({ ...DEFAULT_TYPE_FORM, productNameId: prev.productNameId }));
    }
  };

  const handleCreateCountry = async (event) => {
    event.preventDefault();
    if (!countryForm.name.trim()) {
      toastError('Ülke adı zorunludur.');
      return;
    }

    const isSuccess = await runMutation(
      () =>
        createAdminCountry({
          name: countryForm.name.trim(),
          isoCode: normalizeOptional(countryForm.isoCode),
          sortOrder: parseSortOrder(countryForm.sortOrder),
        }),
      'Ülke eklendi.',
    );

    if (isSuccess) {
      setCountryForm(DEFAULT_COUNTRY_FORM);
    }
  };

  const toggleGroupStatus = (group) =>
    runMutation(
      () => updateAdminProductGroup(group.id, { isActive: !group.isActive }),
      `Ürün grubu ${group.isActive ? 'pasif' : 'aktif'} yapıldı.`,
    );

  const toggleProductNameStatus = (item) =>
    runMutation(
      () => updateAdminProductName(item.id, { isActive: !item.isActive }),
      `Ürün adı ${item.isActive ? 'pasif' : 'aktif'} yapıldı.`,
    );

  const toggleProductTypeStatus = (item) =>
    runMutation(
      () => updateAdminProductType(item.id, { isActive: !item.isActive }),
      `Ürün türü ${item.isActive ? 'pasif' : 'aktif'} yapıldı.`,
    );

  const toggleCountryStatus = (country) =>
    runMutation(
      () => updateAdminCountry(country.id, { isActive: !country.isActive }),
      `Ülke ${country.isActive ? 'pasif' : 'aktif'} yapıldı.`,
    );

  const renderGroupsTab = () => (
    <div className="space-y-5">
      <form onSubmit={handleCreateGroup} className="grid grid-cols-1 lg:grid-cols-5 gap-3 bg-background rounded-md p-4 border border-border">
        <input
          type="text"
          placeholder="Ürün grubu adı"
          value={groupForm.name}
          onChange={(event) => setGroupForm((prev) => ({ ...prev, name: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        />
        <input
          type="text"
          placeholder="Slug (opsiyonel)"
          value={groupForm.slug}
          onChange={(event) => setGroupForm((prev) => ({ ...prev, slug: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        />
        <input
          type="text"
          placeholder="İkon (opsiyonel)"
          value={groupForm.icon}
          onChange={(event) => setGroupForm((prev) => ({ ...prev, icon: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        />
        <input
          type="number"
          min="0"
          placeholder="Sıra"
          value={groupForm.sortOrder}
          onChange={(event) => setGroupForm((prev) => ({ ...prev, sortOrder: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center justify-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
        >
          <Icon name="Plus" size={16} />
          Grup Ekle
        </button>
      </form>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary-100 text-xs text-text-secondary uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Ad</th>
              <th className="px-4 py-3 text-left">Slug</th>
              <th className="px-4 py-3 text-left">Sıra</th>
              <th className="px-4 py-3 text-left">Durum</th>
              <th className="px-4 py-3 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-surface">
            {groups.map((group) => (
              <tr key={group.id}>
                <td className="px-4 py-3 text-sm text-text-primary">{group.name}</td>
                <td className="px-4 py-3 text-xs text-text-secondary">{group.slug}</td>
                <td className="px-4 py-3 text-sm text-text-primary">{group.sortOrder}</td>
                <td className="px-4 py-3"><StatusBadge isActive={group.isActive} /></td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => toggleGroupStatus(group)}
                    disabled={isSaving}
                    className="text-xs font-medium text-primary hover:underline disabled:opacity-60"
                  >
                    {group.isActive ? 'Pasif Et' : 'Aktif Et'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderNamesTab = () => (
    <div className="space-y-5">
      <form onSubmit={handleCreateProductName} className="grid grid-cols-1 lg:grid-cols-6 gap-3 bg-background rounded-md p-4 border border-border">
        <select
          value={productNameForm.groupId}
          onChange={(event) => setProductNameForm((prev) => ({ ...prev, groupId: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        >
          <option value="">Ürün grubu seçin</option>
          {groupOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Ürün adı"
          value={productNameForm.name}
          onChange={(event) => setProductNameForm((prev) => ({ ...prev, name: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        />
        <input
          type="text"
          placeholder="Alt kategori"
          value={productNameForm.subCategory}
          onChange={(event) => setProductNameForm((prev) => ({ ...prev, subCategory: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        />
        <input
          type="text"
          placeholder="Slug (opsiyonel)"
          value={productNameForm.slug}
          onChange={(event) => setProductNameForm((prev) => ({ ...prev, slug: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        />
        <input
          type="number"
          min="0"
          placeholder="Sıra"
          value={productNameForm.sortOrder}
          onChange={(event) => setProductNameForm((prev) => ({ ...prev, sortOrder: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center justify-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
        >
          <Icon name="Plus" size={16} />
          Ürün Adı Ekle
        </button>
      </form>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary-100 text-xs text-text-secondary uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Grup</th>
              <th className="px-4 py-3 text-left">Ürün Adı</th>
              <th className="px-4 py-3 text-left">Alt Kategori</th>
              <th className="px-4 py-3 text-left">Durum</th>
              <th className="px-4 py-3 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-surface">
            {productNames.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-3 text-sm text-text-primary">{item.group?.name || '-'}</td>
                <td className="px-4 py-3 text-sm text-text-primary">{item.name}</td>
                <td className="px-4 py-3 text-sm text-text-secondary">{item.subCategory || '-'}</td>
                <td className="px-4 py-3"><StatusBadge isActive={item.isActive} /></td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => toggleProductNameStatus(item)}
                    disabled={isSaving}
                    className="text-xs font-medium text-primary hover:underline disabled:opacity-60"
                  >
                    {item.isActive ? 'Pasif Et' : 'Aktif Et'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderTypesTab = () => (
    <div className="space-y-5">
      <form onSubmit={handleCreateProductType} className="grid grid-cols-1 lg:grid-cols-5 gap-3 bg-background rounded-md p-4 border border-border">
        <select
          value={productTypeForm.productNameId}
          onChange={(event) =>
            setProductTypeForm((prev) => ({ ...prev, productNameId: event.target.value }))
          }
          className="border border-border rounded-md px-3 py-2 text-sm"
        >
          <option value="">Ürün adı seçin</option>
          {productNameOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Ürün türü"
          value={productTypeForm.name}
          onChange={(event) => setProductTypeForm((prev) => ({ ...prev, name: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        />
        <input
          type="text"
          placeholder="Slug (opsiyonel)"
          value={productTypeForm.slug}
          onChange={(event) => setProductTypeForm((prev) => ({ ...prev, slug: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        />
        <input
          type="number"
          min="0"
          placeholder="Sıra"
          value={productTypeForm.sortOrder}
          onChange={(event) => setProductTypeForm((prev) => ({ ...prev, sortOrder: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center justify-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
        >
          <Icon name="Plus" size={16} />
          Tür Ekle
        </button>
      </form>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary-100 text-xs text-text-secondary uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Grup / Ürün</th>
              <th className="px-4 py-3 text-left">Tür</th>
              <th className="px-4 py-3 text-left">Sıra</th>
              <th className="px-4 py-3 text-left">Durum</th>
              <th className="px-4 py-3 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-surface">
            {productTypes.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-3 text-sm text-text-secondary">
                  {(item.productName?.group?.name || '-') + ' / ' + (item.productName?.name || '-')}
                </td>
                <td className="px-4 py-3 text-sm text-text-primary">{item.name}</td>
                <td className="px-4 py-3 text-sm text-text-primary">{item.sortOrder}</td>
                <td className="px-4 py-3"><StatusBadge isActive={item.isActive} /></td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => toggleProductTypeStatus(item)}
                    disabled={isSaving}
                    className="text-xs font-medium text-primary hover:underline disabled:opacity-60"
                  >
                    {item.isActive ? 'Pasif Et' : 'Aktif Et'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCountriesTab = () => (
    <div className="space-y-5">
      <form onSubmit={handleCreateCountry} className="grid grid-cols-1 lg:grid-cols-4 gap-3 bg-background rounded-md p-4 border border-border">
        <input
          type="text"
          placeholder="Ülke adı"
          value={countryForm.name}
          onChange={(event) => setCountryForm((prev) => ({ ...prev, name: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        />
        <input
          type="text"
          placeholder="ISO Kod (opsiyonel)"
          value={countryForm.isoCode}
          onChange={(event) => setCountryForm((prev) => ({ ...prev, isoCode: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm uppercase"
        />
        <input
          type="number"
          min="0"
          placeholder="Sıra"
          value={countryForm.sortOrder}
          onChange={(event) => setCountryForm((prev) => ({ ...prev, sortOrder: event.target.value }))}
          className="border border-border rounded-md px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center justify-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
        >
          <Icon name="Plus" size={16} />
          Ülke Ekle
        </button>
      </form>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary-100 text-xs text-text-secondary uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Ülke</th>
              <th className="px-4 py-3 text-left">ISO</th>
              <th className="px-4 py-3 text-left">Sıra</th>
              <th className="px-4 py-3 text-left">Durum</th>
              <th className="px-4 py-3 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-surface">
            {countries.map((country) => (
              <tr key={country.id}>
                <td className="px-4 py-3 text-sm text-text-primary">{country.name}</td>
                <td className="px-4 py-3 text-sm text-text-secondary">{country.isoCode || '-'}</td>
                <td className="px-4 py-3 text-sm text-text-primary">{country.sortOrder}</td>
                <td className="px-4 py-3"><StatusBadge isActive={country.isActive} /></td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => toggleCountryStatus(country)}
                    disabled={isSaving}
                    className="text-xs font-medium text-primary hover:underline disabled:opacity-60"
                  >
                    {country.isActive ? 'Pasif Et' : 'Aktif Et'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderTabContent = () => {
    if (activeTab === 'groups') {
      return renderGroupsTab();
    }
    if (activeTab === 'names') {
      return renderNamesTab();
    }
    if (activeTab === 'types') {
      return renderTypesTab();
    }
    return renderCountriesTab();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16 lg:pt-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-text-primary font-heading">Genel Yönetim Ayarları</h1>
            <p className="text-text-secondary mt-2">
              Superadmin tarafından yönetilen ana veriler (aktif/pasif) bu ekrandan yönetilir ve pazaryeri kullanıcılarında aynı şekilde listelenir.
            </p>
          </div>

          <section className="bg-surface border border-border rounded-lg shadow-elevation-1">
            <div className="border-b border-border px-4 py-3 overflow-x-auto">
              <div className="inline-flex gap-2 min-w-full lg:min-w-0">
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                      activeTab === tab.key
                        ? 'bg-primary text-white'
                        : 'text-text-secondary hover:text-text-primary hover:bg-secondary-100'
                    }`}
                  >
                    <Icon name={tab.icon} size={16} />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 lg:p-6">
              {isLoading ? (
                <div className="flex items-center gap-3 text-text-secondary py-8">
                  <Icon name="Loader" size={18} className="animate-spin" />
                  Veriler yükleniyor...
                </div>
              ) : (
                renderTabContent()
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default GeneralManagementSettings;
